import React, { useState } from "react";
import * as Icons from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { localHHMM } from "@/lib/people";
import { toast } from "sonner";

export function CorrectionDialog({ orgId, entry, tz, today, onClose, onSaved }) {
  const [f, setF] = useState({
    date: entry?.local_date || today, clock_in: localHHMM(entry?.clock_in, tz), clock_out: localHHMM(entry?.clock_out, tz), reason: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    if (!f.reason.trim()) return toast.error("Explain what needs correcting");
    setSaving(true);
    try {
      await api.post(`/workspace/clock/corrections?org_id=${orgId}`, { ...f, entry_id: entry?.id || null, clock_out: f.clock_out || null });
      toast.success("Correction sent for approval");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="correction-dialog">
        <DialogHeader>
          <DialogTitle>{entry ? "Correct this record" : "Add a missing record"}</DialogTitle>
          <DialogDescription>Someone other than you approves it before your record changes. The original times stay in the history.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5">
          <div><Label>Date</Label><Input type="date" value={f.date} disabled={!!entry} onChange={(e) => set("date", e.target.value)} className="mt-1" data-testid="correction-date" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Clock in</Label><Input type="time" value={f.clock_in} onChange={(e) => set("clock_in", e.target.value)} className="mt-1" data-testid="correction-in" /></div>
            <div><Label>Clock out</Label><Input type="time" value={f.clock_out} onChange={(e) => set("clock_out", e.target.value)} className="mt-1" data-testid="correction-out" /></div>
          </div>
          <div><Label>Reason</Label><Textarea rows={2} value={f.reason} onChange={(e) => set("reason", e.target.value)} className="mt-1" placeholder="e.g. Forgot to clock out — finished at 16:30" data-testid="correction-reason" /></div>
          <p className="text-xs text-[#1E2D44]/45">Times are in {tz}.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#FFB000] text-[#0B1729] hover:bg-[#E69E00]" data-testid="correction-submit-btn">
            {saving ? <Icons.Loader2 className="animate-spin" size={16} /> : "Send for approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
